import React, { useMemo } from 'react';
import { Auth0Context } from '@auth0/auth0-react';
import { auth0ClaimKey, deploymentConfig } from '../config/deployment';

// The dev staff identities below live at the deployment's (first) staff email
// domain, so a dev session looks like that deployment's staff rather than
// carrying one organization's domain in source.
const DEV_STAFF_EMAIL_DOMAIN = deploymentConfig.staffEmailDomains[0];

// Set VITE_DEV_PLATFORM_ADMIN=true to impersonate a platform admin in dev mode
// (needed to access /admin/* routes without real Auth0 login). Defaults to the
// regular customer dev user.
const isDevPlatformAdmin = import.meta.env.VITE_DEV_PLATFORM_ADMIN === 'true';

type DevPlatformRole = 'admin' | 'operator' | null;

function getDevPlatformRole(): DevPlatformRole {
  if (isDevPlatformAdmin) {
    const lsRole = localStorage.getItem('dev-platform-role');
    if (lsRole === 'operator') return 'operator';
    return 'admin';
  }
  return null;
}

// Set localStorage 'dev-signed-out' to 'true' to make the dev context report
// unauthenticated (no user), so public routes like /register render instead of
// the authenticated redirect. Used by registration E2E specs via addInitScript.
function isDevSignedOut(): boolean {
  return localStorage.getItem('dev-signed-out') === 'true';
}

// Set localStorage 'dev-onboarding-pending' to 'true' to make the dev user's
// onboardingCompleted claim resolve false. Both approved signals must clear too
// (top-level claim + app_metadata) — useAuthStatus short-circuits
// onboardingCompleted true for approved users — so ProtectedRoute lands on the
// onboarding page. Used by the registration walkthrough E2E spec.
// No-op in VITE_DEV_PLATFORM_ADMIN=true sessions: useAuthStatus's isAdmin
// short-circuit keeps onboardingCompleted true for the admin dev user
// regardless of this switch, and /admin/* routes are guarded by isPlatformUser
// (never ProtectedRoute), so admin/operator sessions never hit an onboarding
// redirect. Point onboarding-pending E2E scenarios at a non-admin (customer)
// dev session — i.e. a spec that runs outside the chromium-admin /
// chromium-operator Playwright projects.
function isDevOnboardingPending(): boolean {
  return localStorage.getItem('dev-onboarding-pending') === 'true';
}

function buildDevUser(role: DevPlatformRole) {
  if (role === 'operator') {
    return {
      sub: 'auth0|dev-platform-operator',
      email: `operator@${DEV_STAFF_EMAIL_DOMAIN}`,
      name: 'Dev Operator',
      [auth0ClaimKey('roles')]: ['operator'],
      [auth0ClaimKey('org_id')]: undefined,
      [auth0ClaimKey('approved')]: true,
      [auth0ClaimKey('platform_role')]: 'operator',
      [auth0ClaimKey('app_metadata')]: {
        approved: true,
        onboardingCompleted: true,
      },
    };
  }

  if (role === 'admin') {
    return {
      sub: 'auth0|dev-platform-admin',
      email: `ken@${DEV_STAFF_EMAIL_DOMAIN}`,
      name: 'Ken Williams (Admin)',
      [auth0ClaimKey('roles')]: ['admin'],
      [auth0ClaimKey('org_id')]: undefined,
      [auth0ClaimKey('approved')]: true,
      [auth0ClaimKey('platform_role')]: 'admin',
      [auth0ClaimKey('app_metadata')]: {
        approved: true,
        onboardingCompleted: true,
      },
    };
  }

  return {
    sub: 'google-oauth2|105143601137372851412',
    email: 'ken.lee.williams@gmail.com',
    name: 'Ken Williams',
    [auth0ClaimKey('roles')]: ['owner'],
    [auth0ClaimKey('org_id')]: undefined,
    [auth0ClaimKey('approved')]: true,
    [auth0ClaimKey('platform_role')]: null,
    [auth0ClaimKey('app_metadata')]: {
      approved: true,
      onboardingCompleted: true,
    },
  };
}

const noop = () => Promise.resolve();

/**
 * Dev-only Auth0 context provider that bypasses real OIDC.
 * Provides a pre-authenticated user so the app loads without Auth0.
 */
export const DevAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const signedOut = useMemo(() => isDevSignedOut(), []);
  const devUser = useMemo(() => {
    const user = buildDevUser(getDevPlatformRole());
    if (!isDevOnboardingPending()) return user;
    return {
      ...user,
      [auth0ClaimKey('approved')]: false,
      [auth0ClaimKey('app_metadata')]: {
        approved: false,
        onboardingCompleted: false,
      },
    };
  }, []);

  const contextValue = {
    isAuthenticated: !signedOut,
    isLoading: false,
    user: signedOut ? undefined : devUser,
    error: undefined,
    getAccessTokenSilently: (() => Promise.resolve('dev-token')) as any,
    getAccessTokenWithPopup: (() => Promise.resolve('dev-token')) as any,
    getIdTokenClaims: (() => Promise.resolve(undefined)) as any,
    loginWithCustomTokenExchange: noop as any,
    exchangeToken: noop as any,
    loginWithRedirect: noop as any,
    loginWithPopup: noop as any,
    connectAccountWithRedirect: noop as any,
    logout: noop as any,
    handleRedirectCallback: (() => Promise.resolve({})) as any,
    getDpopNonce: (() => undefined) as any,
    setDpopNonce: noop as any,
    generateDpopProof: noop as any,
    createFetcher: noop as any,
    getConfiguration: (() => ({ domain: 'dev', clientId: 'dev' })) as any,
    customTokenExchange: noop as any,
    passkey: noop as any,
    myAccount: noop as any,
    mfa: {
      getAuthenticators: noop,
      enroll: noop,
      challenge: noop,
      verify: noop,
      getEnrollmentFactors: noop,
    } as any,
  };

  return (
    <Auth0Context value={contextValue}>
      {children}
    </Auth0Context>
  );
};
